import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Check,
  X,
  ChevronDown,
  ChevronUp,
  Star,
  ArrowLeft,
  UserPlus,
  UserMinus,
  HeartPulse,
  Phone,
  PhoneOff,
} from 'lucide-react';
import { esCanceladaPorElClub, ETIQUETA_CANCELADA, COLORES_CANCELADA } from '../../lib/quienLleva';
import { supabase } from '../../services/supabase';
import { leerFichaMedica, leerFlagsFichaMedica } from '../../services/fichaMedica';
import { listaEnCastellano } from '../../lib/leerVarias';
import {
  enlaceTel,
  porAlumno,
  rotuloDeContacto,
  aQuienLlamar,
  type ContactoDeAlumno,
} from '../../lib/contactoDeLaSesion';
import { useAuthStore } from '../../store/authStore';
import type { Database } from '../../types/database.types';

type EnrollmentRow = Database['public']['Tables']['enrollments']['Row'];

// Embed que pide la consulta: enrollments + perfil del alumno (id, full_name).
// PostgREST devuelve embed many-to-one como objeto.
// mig 282: `medical_info` YA NO viaja aquí. La lista solo necesita saber quién
// tiene ficha (`tiene_ficha`), y el contenido se pide por la RPC auditada al
// abrirla.
type EnrollmentWithUser = Pick<EnrollmentRow, 'id' | 'attendance'> & {
  user: { id: string; full_name: string } | null;
};

// PR-EV1: la valoración "por sesión" ahora es una NOTA RÁPIDA (quick_notes):
// rating 1-10 + nota corta, una por (sesión, alumno, entrenador).
type QuickNoteBrief = { student_id: string; rating: number; note: string | null };

// Resultados de las RPCs staff (mismo convenio que en SesionesPage).
type StaffAddResult = { ok: true } | { ok: false; error: string };
type StaffRemoveResult = { ok: true } | { ok: false; error: string };

interface Student {
  enrollment_id: string;
  user_id: string;
  full_name: string;
  attended: boolean | null;
  rating: number | null;
  // Solo si la tiene, no lo que dice. El contenido se lee al abrir.
  tiene_ficha: boolean;
}

interface SessionInfo {
  date: string;
  time_start: string;
  time_end: string;
  group_name: string;
  group_level: string;
  /**
   * La canceló el club. El `select` de arriba traía todo menos el estado, así
   * que el detalle ofrecía pasar lista de una sesión que no se dio. Se pinta
   * marcada y NO se oculta, copiado de la pantalla del alumno.
   */
  cancelada: boolean;
}

const LEVEL_COLORS: Record<string, string> = {
  basico: '#4CAF50',
  medio: '#2196F3',
  avanzado: '#FF9800',
  tecnificacion: '#FFF200',
};
const LEVEL_LABELS: Record<string, string> = {
  basico: 'Básico',
  medio: 'Medio',
  avanzado: 'Avanzado',
  tecnificacion: 'Tecnificación',
};

// Esta es la pantalla en la que el entrenador pone la asistencia y las notas.
// Las cuatro lecturas tiraban el `error`, y la lista de inscritos vacía se
// remataba con «No hay alumnos inscritos»: indistinguible de una sesión a la
// que de verdad no se ha apuntado nadie.
const QUE = {
  sesion: 'los datos de la sesión',
  inscritos: 'los inscritos',
  notas: 'las notas y valoraciones ya puestas',
  alumnos: 'la lista de alumnos del club',
  // mig 332 · si esto falla, la fila NO puede decir «sin teléfono»: diría que
  // no hay número cuando lo que pasa es que no se ha podido leer, y de lo
  // segundo sí se puede hacer algo (recargar, o salir a buscarlo).
  contacto: 'los teléfonos de contacto',
} as const;

export default function MonitorSessionDetailPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { profile } = useAuthStore();

  const [info, setInfo] = useState<SessionInfo | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changedAttend, setChangedAttend] = useState<Record<string, boolean | null>>({});
  const [showRatings, setShowRatings] = useState(false);
  const [ratings, setRatings] = useState<Record<string, number>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [expandedNote, setExpandedNote] = useState<string | null>(null);
  const [savingRatings, setSavingRatings] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savedRatings, setSavedRatings] = useState(false);
  // Ni la asistencia ni las valoraciones miraban su resultado, y las dos
  // anunciaban éxito igual. La de asistencia además hacía setChangedAttend({}),
  // tirando las marcas pendientes: el entrenador perdía el trabajo Y creía
  // que estaba guardado.
  const [saveError, setSaveError] = useState('');
  const [addStudentModal, setAddStudentModal] = useState(false);
  const [allStudents, setAllStudents] = useState<{ id: string; full_name: string }[]>([]);
  const [bloquesFallidos, setBloquesFallidos] = useState<string[]>([]);
  // mig 332 · el teléfono de casa de cada inscrito, por `contacto_de_la_sesion`.
  // Un mapa vacío con la lectura caída y un mapa vacío con la sesión vacía son
  // lo mismo aquí, así que quien distingue es `QUE.contacto` en `fallidos`.
  const [contactos, setContactos] = useState<Record<string, ContactoDeAlumno>>({});
  const [addStudentId, setAddStudentId] = useState('');
  const [addingStudent, setAddingStudent] = useState(false);
  const [addStudentError, setAddStudentError] = useState('');
  const [removingId, setRemovingId] = useState<string | null>(null);
  // mig 281: el contenido llega cuando se abre el modal, no antes. `estado`
  // existe porque ahora hay una llamada de red por medio y el usuario tiene
  // que ver la diferencia entre «cargando», «no hay nada» y «no se pudo leer».
  const [medicalModal, setMedicalModal] = useState<{
    name: string;
    info: string | null;
    estado: 'cargando' | 'ok' | 'error';
    mensaje?: string;
  } | null>(null);

  // Abrir la ficha ES el acto que se audita. Se muestra el modal en
  // «cargando» antes de tener el dato para que el clic tenga respuesta
  // inmediata y para que nunca se pinte contenido viejo de otro alumno.
  const abrirFicha = useCallback(async (userId: string, nombre: string) => {
    setMedicalModal({ name: nombre, info: null, estado: 'cargando' });
    const r = await leerFichaMedica(userId);
    setMedicalModal(
      r.estado === 'ok'
        ? { name: nombre, info: r.ficha, estado: 'ok' }
        : { name: nombre, info: null, estado: 'error', mensaje: r.mensaje }
    );
  }, []);

  const load = useCallback(async () => {
    if (!sessionId) return;

    const fallidos: string[] = [];

    const { data: sess, error: errSess } = await supabase
      .from('sessions')
      .select('date, time_start, time_end, status, group:groups(name, level)')
      .eq('id', sessionId)
      .single();

    if (errSess) fallidos.push(QUE.sesion);

    if (sess) {
      const group = (sess as unknown as { group: { name: string; level: string } | null }).group;
      setInfo({
        date: sess.date,
        time_start: sess.time_start,
        time_end: sess.time_end,
        group_name: group?.name ?? 'Sesión',
        group_level: group?.level ?? 'basico',
        cancelada: esCanceladaPorElClub(sess),
      });
    }

    const { data: enrolls, error: errEnrolls } = await supabase
      .from('enrollments')
      .select('id, attendance, user:profiles!enrollments_user_id_fkey(id, full_name)')
      .eq('session_id', sessionId)
      .eq('status', 'confirmed');

    // ⚠️ Si esto falla, la pantalla decía «No hay alumnos inscritos» y el
    //    entrenador cerraba la sesión sin poner asistencia a nadie.
    if (errEnrolls) fallidos.push(QUE.inscritos);

    const enrollList = (enrolls ?? []) as unknown as EnrollmentWithUser[];

    // mig 282: la lista ya NO hidrata el contenido de la ficha. Pide solo el
    // flag `tiene_ficha_medica`, que sale del mismo gate que el contenido y por
    // tanto no puede ser más ancho. El dato se lee al abrir, por la RPC
    // auditada — antes, pintar esta pantalla leía la ficha de todos los
    // inscritos sin que quedara rastro de nada.
    const userIds = enrollList
      .map((e) => e.user?.id)
      .filter((id): id is string => typeof id === 'string' && id.length > 0);
    const { conFicha } = await leerFlagsFichaMedica(userIds);

    // mig 332 · A QUIÉN SE LLAMA. Va por RPC y no por embed a propósito: el
    // teléfono de casa vive en `guardians`, que un monitor no puede leer —son
    // 45 de los 52 números—, y el perfil del tutor tampoco. La RPC es SECURITY
    // DEFINER y devuelve el teléfono sin abrir ni la tabla ni el perfil.
    //
    // Se pide con el id de la sesión y no con la lista de alumnos: la propia
    // RPC se queda con los inscritos CONFIRMADOS, que son los mismos que pinta
    // esta lista. Una segunda lista de ids aquí sería una segunda verdad sobre
    // quién está en la sesión.
    const { data: contactoData, error: errContacto } = await supabase.rpc('contacto_de_la_sesion', {
      p_session_id: sessionId,
    });
    if (errContacto) fallidos.push(QUE.contacto);
    setContactos(porAlumno((contactoData ?? []) as ContactoDeAlumno[]));

    const studentList: Student[] = enrollList
      .map((e) => ({
        enrollment_id: e.id,
        user_id: e.user?.id ?? '',
        full_name: e.user?.full_name ?? 'Alumno',
        attended: e.attendance,
        rating: null,
        tiene_ficha: conFicha.has(e.user?.id ?? ''),
      }))
      .sort((a, b) => a.full_name.localeCompare(b.full_name, 'es'));

    if (profile && studentList.length > 0) {
      const { data: evalData, error: errNotes } = await supabase
        .from('quick_notes')
        .select('student_id, rating, note')
        .eq('session_id', sessionId)
        .eq('trainer_id', profile.id)
        .in(
          'student_id',
          studentList.map((s) => s.user_id)
        );

      // Sin esto, las notas y estrellas YA puestas salen vacías, y el
      // entrenador las vuelve a escribir encima creyendo que faltaban.
      if (errNotes) fallidos.push(QUE.notas);

      const ratingMap: Record<string, number> = {};
      const notesMap: Record<string, string> = {};
      ((evalData ?? []) as QuickNoteBrief[]).forEach((ev) => {
        ratingMap[ev.student_id] = ev.rating ?? 0;
        notesMap[ev.student_id] = ev.note ?? '';
      });
      setRatings(ratingMap);
      setNotes(notesMap);
      studentList.forEach((s) => {
        s.rating = ratingMap[s.user_id] ?? null;
      });
    }

    setStudents(studentList);
    setChangedAttend({});
    setBloquesFallidos(fallidos);
    setLoading(false);
  }, [sessionId, profile]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    // De aquí sale el desplegable de «Añadir alumno»: vacío por un fallo de
    // lectura se lee como que no hay a quién añadir.
    supabase
      .from('profiles')
      .select('id, full_name')
      .eq('role', 'alumno')
      .eq('active', true)
      .order('full_name')
      .then(({ data, error }) => {
        if (error) {
          setBloquesFallidos((prev) =>
            prev.includes(QUE.alumnos) ? prev : [...prev, QUE.alumnos]
          );
          return;
        }
        setAllStudents(data ?? []);
      });
  }, []);

  async function handleAddStudent() {
    if (!sessionId || !addStudentId) return;
    setAddingStudent(true);
    setAddStudentError('');
    const { data, error } = await supabase.rpc('staff_add_student_to_session', {
      p_student_id: addStudentId,
      p_session_id: sessionId,
      p_consume_credit: true,
    });
    setAddingStudent(false);
    const result = data as StaffAddResult | null;
    if (error || !result?.ok) {
      const message = result && !result.ok ? result.error : error?.message;
      setAddStudentError(message ?? 'Error al inscribir');
      return;
    }
    setAddStudentModal(false);
    setAddStudentId('');
    load();
  }

  async function handleRemoveStudent(studentId: string, name: string) {
    if (!sessionId) return;
    if (!confirm(`¿Dar de baja a ${name}? Se le devolverá la sesión al bono si correspondía.`))
      return;
    setRemovingId(studentId);
    const { data, error } = await supabase.rpc('staff_remove_student_from_session', {
      p_student_id: studentId,
      p_session_id: sessionId,
    });
    setRemovingId(null);
    const result = data as StaffRemoveResult | null;
    if (error || !result?.ok) {
      const message = result && !result.ok ? result.error : error?.message;
      alert(message ?? 'Error al dar de baja');
      return;
    }
    load();
  }

  function setAttendance(enrollmentId: string, value: boolean | null) {
    setChangedAttend((prev) => ({ ...prev, [enrollmentId]: value }));
    setStudents((prev) =>
      prev.map((s) => (s.enrollment_id === enrollmentId ? { ...s, attended: value } : s))
    );
  }

  async function handleSaveAttendance() {
    const updates = Object.entries(changedAttend);
    if (!updates.length) return;
    setSaving(true);
    setSaveError('');
    const resultados = await Promise.all(
      updates.map(([id, attended]) =>
        supabase.from('enrollments').update({ attendance: attended }).eq('id', id)
      )
    );
    setSaving(false);
    // Van en paralelo, así que unas pueden entrar y otras no. Se cuenta
    // cuántas fallaron y NO se vacían los cambios pendientes: el botón sigue
    // ofreciendo reintentar con lo que falta.
    const fallidas = resultados.filter((r) => r.error);
    if (fallidas.length) {
      setSaveError(
        `No se pudo guardar la asistencia de ${fallidas.length} de ${updates.length} alumnos: ${fallidas[0].error?.message}`
      );
      return;
    }
    setChangedAttend({});
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }

  async function handleSaveRatings() {
    if (!profile || !sessionId) return;
    setSavingRatings(true);
    // Solo se guardan notas rápidas con un rating asignado (rating es NOT NULL 1-10).
    const upserts = students
      .filter((s) => (ratings[s.user_id] ?? 0) >= 1)
      .map((s) => ({
        student_id: s.user_id,
        trainer_id: profile.id,
        session_id: sessionId,
        rating: ratings[s.user_id],
        note: notes[s.user_id]?.trim() || null,
      }));
    setSaveError('');
    if (upserts.length) {
      const { error } = await supabase.from('quick_notes').upsert(upserts, {
        onConflict: 'session_id,student_id,trainer_id',
      });
      // Sin esto, load() recargaba desde la base —borrando de la pantalla las
      // valoraciones que no habían entrado— y el botón decía
      // "✓ Valoraciones guardadas".
      if (error) {
        setSavingRatings(false);
        setSaveError(`No se pudieron guardar las valoraciones: ${error.message}`);
        return;
      }
    }
    setSavingRatings(false);
    setSavedRatings(true);
    load();
    setTimeout(() => setSavedRatings(false), 3000);
  }

  const presentCount = students.filter((s) => s.attended === true).length;
  const absentCount = students.filter((s) => s.attended === false).length;
  const pendingCount = students.filter((s) => s.attended === null).length;

  const formatDate = (d: string) =>
    new Date(`${d}T12:00:00`).toLocaleDateString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gold-primary font-oswald animate-pulse">Cargando...</div>
      </div>
    );
  }

  const levelColor = LEVEL_COLORS[info?.group_level ?? ''] ?? '#FFF200';

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {bloquesFallidos.length > 0 && (
        <div
          className="card border-error/40 bg-error/5"
          data-testid="sesion-fallidos"
          role="status"
        >
          <p className="text-error text-sm">
            No se han podido cargar {listaEnCastellano([...bloquesFallidos])}. No cierres la sesión
            dándola por puesta.
          </p>
        </div>
      )}

      {/* Back + header */}
      <div>
        <button
          onClick={() => navigate('/monitor/agenda')}
          className="flex items-center gap-2 text-gold-primary font-inter text-sm mb-4 hover:opacity-80 transition-opacity"
        >
          <ArrowLeft size={16} /> Volver a la agenda
        </button>

        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-oswald text-2xl text-white tracking-wide">{info?.group_name}</h2>
            <p className="font-inter text-text-secondary text-sm mt-1 capitalize">
              {info ? formatDate(info.date) : ''} · {info?.time_start.slice(0, 5)}–
              {info?.time_end.slice(0, 5)}
            </p>
          </div>
          {/* El estado, y ANTES del nivel: es lo que explica que la lista esté
              vacía y que no quede nada por pasar. «La sesión cancelada por el
              club manda sobre el estado del enrollment» —de la pantalla del
              alumno, literal. */}
          {info?.cancelada && (
            <span
              data-testid="detalle-cancelada"
              className="text-xs font-inter font-semibold uppercase tracking-wide px-3 py-1 rounded-full mr-2"
              style={{
                color: COLORES_CANCELADA.text,
                backgroundColor: COLORES_CANCELADA.bg,
                border: `1px solid ${COLORES_CANCELADA.border}`,
              }}
            >
              {ETIQUETA_CANCELADA}
            </span>
          )}
          <span
            className="text-xs font-inter font-semibold uppercase tracking-wide px-3 py-1 rounded-full"
            style={{
              color: levelColor,
              backgroundColor: `${levelColor}20`,
              border: `1px solid ${levelColor}40`,
            }}
          >
            {LEVEL_LABELS[info?.group_level ?? ''] ?? info?.group_level}
          </span>
        </div>
      </div>

      {/* Stats bar */}
      <div className="bg-bg-surface border border-bg-border rounded-xl px-6 py-4 flex items-center gap-8">
        <div>
          <p className="font-oswald text-2xl text-[#4CAF50]">{presentCount}</p>
          <p className="font-inter text-text-secondary text-xs">presentes</p>
        </div>
        <div>
          <p className="font-oswald text-2xl text-[#FF4444]">{absentCount}</p>
          <p className="font-inter text-text-secondary text-xs">ausentes</p>
        </div>
        {pendingCount > 0 && (
          <div>
            <p className="font-oswald text-2xl text-[#FF9800]">{pendingCount}</p>
            <p className="font-inter text-text-secondary text-xs">pendientes</p>
          </div>
        )}
        <div className="ml-auto">
          <p className="font-oswald text-2xl text-white">{students.length}</p>
          <p className="font-inter text-text-secondary text-xs">total</p>
        </div>
      </div>

      {/* Attendance section */}
      <div className="card space-y-1">
        <div className="flex items-center justify-between mb-4">
          <p className="font-oswald text-xs text-text-secondary tracking-widest">ASISTENCIA</p>
          <button
            onClick={() => {
              setAddStudentId('');
              setAddStudentError('');
              setAddStudentModal(true);
            }}
            className="flex items-center gap-1.5 text-gold-primary hover:text-white text-xs transition-colors"
          >
            <UserPlus size={14} /> Añadir alumno
          </button>
        </div>
        {students.length === 0 ? (
          <p
            className={`font-inter text-sm text-center py-4 ${
              bloquesFallidos.includes(QUE.inscritos) ? 'text-error' : 'text-text-secondary'
            }`}
            data-testid="sesion-vacio-inscritos"
          >
            {bloquesFallidos.includes(QUE.inscritos)
              ? 'No se han podido cargar los inscritos.'
              : 'No hay alumnos inscritos'}
          </p>
        ) : (
          students.map((s, i) => (
            <div
              key={s.enrollment_id}
              className="flex items-center justify-between py-2 px-0 border-b border-bg-border last:border-0"
            >
              <div className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-bg-elevated flex items-center justify-center text-xs text-text-secondary font-inter">
                  {i + 1}
                </span>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-inter text-white text-sm">{s.full_name}</p>
                    {s.tiene_ficha && (
                      <button
                        onClick={() => void abrirFicha(s.user_id, s.full_name)}
                        title="Información médica"
                        className="text-error hover:text-error/80 transition-colors"
                      >
                        <HeartPulse size={14} />
                      </button>
                    )}
                  </div>
                  {/* mig 332 · A QUIÉN SE LLAMA, en la fila. El rótulo va
                      SIEMPRE, también cuando no hay número: un botón que no
                      aparece no dice que no haya teléfono, no dice nada, y esa
                      diferencia es la que hace que alguien lo busque fuera o se
                      quede esperando. Y los tres estados son tres, no dos:
                      · hay número        → se marca, y se dice de quién es;
                      · no hay ninguno    → «sin teléfono», que es un dato;
                      · no se pudo leer   → se dice eso, no «sin teléfono». */}
                  {bloquesFallidos.includes(QUE.contacto) ? (
                    <p
                      className="font-inter text-error text-xs"
                      data-testid={`sesion-contacto-ilegible-${s.user_id}`}
                    >
                      Teléfono no leído
                    </p>
                  ) : rotuloDeContacto(contactos[s.user_id]) ? (
                    <a
                      href={enlaceTel(aQuienLlamar(contactos[s.user_id])!.telefono)}
                      data-testid={`sesion-llamar-${s.user_id}`}
                      className="font-inter text-xs text-[#4CAF50] hover:underline inline-flex items-center gap-1"
                    >
                      <Phone size={11} />
                      {rotuloDeContacto(contactos[s.user_id])}
                    </a>
                  ) : (
                    <p
                      className="font-inter text-text-secondary text-xs inline-flex items-center gap-1"
                      data-testid={`sesion-sin-telefono-${s.user_id}`}
                    >
                      <PhoneOff size={11} /> Sin teléfono
                    </p>
                  )}
                  {s.rating != null && (
                    <p className="font-inter text-gold-primary text-xs">⭐ {s.rating}/10</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    setAttendance(s.enrollment_id, s.attended === false ? null : false)
                  }
                  className={`w-9 h-9 rounded-lg border flex items-center justify-center transition-colors ${
                    s.attended === false
                      ? 'bg-[#FF4444] border-[#FF4444]'
                      : 'bg-bg-elevated border-bg-border hover:border-[#FF4444]/50'
                  }`}
                >
                  <X
                    size={16}
                    className={s.attended === false ? 'text-bg-primary' : 'text-text-secondary'}
                  />
                </button>
                <button
                  onClick={() => setAttendance(s.enrollment_id, s.attended === true ? null : true)}
                  className={`w-9 h-9 rounded-lg border flex items-center justify-center transition-colors ${
                    s.attended === true
                      ? 'bg-[#4CAF50] border-[#4CAF50]'
                      : 'bg-bg-elevated border-bg-border hover:border-[#4CAF50]/50'
                  }`}
                >
                  <Check
                    size={16}
                    className={s.attended === true ? 'text-bg-primary' : 'text-text-secondary'}
                  />
                </button>
                <button
                  onClick={() => handleRemoveStudent(s.user_id, s.full_name)}
                  disabled={removingId === s.user_id}
                  title="Dar de baja"
                  className="w-9 h-9 rounded-lg border border-bg-border bg-bg-elevated flex items-center justify-center hover:border-error/50 transition-colors disabled:opacity-50"
                >
                  <UserMinus size={15} className="text-error" />
                </button>
              </div>
            </div>
          ))
        )}

        {saveError && (
          <p
            className="text-error text-sm border border-error/30 rounded px-3 py-2 mt-3"
            data-testid="sesion-save-error"
          >
            {saveError}
          </p>
        )}
        {students.length > 0 && (
          <div className="pt-4">
            <button
              onClick={handleSaveAttendance}
              disabled={saving || Object.keys(changedAttend).length === 0}
              className="btn-gold w-full"
            >
              {saving
                ? 'Guardando...'
                : saved
                  ? '✓ Asistencia guardada'
                  : Object.keys(changedAttend).length > 0
                    ? `Guardar asistencia (${Object.keys(changedAttend).length} cambios)`
                    : 'Sin cambios pendientes'}
            </button>
          </div>
        )}
      </div>

      {/* Ratings section */}
      {students.length > 0 && (
        <div className="card">
          <button
            onClick={() => setShowRatings((v) => !v)}
            className="w-full flex items-center gap-3 text-left"
          >
            <Star size={16} className="text-gold-primary" />
            <span className="font-oswald text-gold-primary text-sm uppercase tracking-widest flex-1">
              Valorar sesión por alumno
            </span>
            {showRatings ? (
              <ChevronUp size={16} className="text-gold-primary" />
            ) : (
              <ChevronDown size={16} className="text-gold-primary" />
            )}
          </button>

          {showRatings && (
            <div className="mt-4 space-y-4 border-t border-bg-border pt-4">
              <p className="font-inter text-text-secondary text-xs">
                Puntuación general del alumno en esta sesión (1–10)
              </p>
              {students.map((s) => (
                <div key={s.user_id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-inter text-white text-sm">{s.full_name}</p>
                      <button
                        onClick={() => setExpandedNote((n) => (n === s.user_id ? null : s.user_id))}
                        className="text-text-secondary text-xs hover:text-gold-primary transition-colors"
                      >
                        💬 {expandedNote === s.user_id ? 'ocultar nota' : 'añadir nota'}
                      </button>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() =>
                          setRatings((prev) => ({
                            ...prev,
                            [s.user_id]: Math.max(1, (prev[s.user_id] ?? 5) - 1),
                          }))
                        }
                        className="w-8 h-8 rounded-lg bg-bg-elevated border border-bg-border flex items-center justify-center text-white font-oswald text-lg hover:border-gold-primary/50 transition-colors"
                      >
                        −
                      </button>
                      <span className="font-oswald text-2xl text-gold-primary w-8 text-center">
                        {ratings[s.user_id] ?? '—'}
                      </span>
                      <button
                        onClick={() =>
                          setRatings((prev) => ({
                            ...prev,
                            [s.user_id]: Math.min(10, (prev[s.user_id] ?? 5) + 1),
                          }))
                        }
                        className="w-8 h-8 rounded-lg bg-bg-elevated border border-bg-border flex items-center justify-center text-white font-oswald text-lg hover:border-gold-primary/50 transition-colors"
                      >
                        +
                      </button>
                    </div>
                  </div>
                  {expandedNote === s.user_id && (
                    <textarea
                      className="input w-full min-h-[60px] resize-none text-sm"
                      value={notes[s.user_id] ?? ''}
                      onChange={(e) =>
                        setNotes((prev) => ({ ...prev, [s.user_id]: e.target.value }))
                      }
                      placeholder="Observación breve..."
                      maxLength={200}
                    />
                  )}
                </div>
              ))}
              <button
                onClick={handleSaveRatings}
                disabled={savingRatings}
                className="w-full py-3 rounded-lg border border-gold-primary/40 bg-gold-primary/10 text-gold-primary font-oswald text-sm uppercase tracking-widest hover:bg-gold-primary/20 transition-colors disabled:opacity-50"
              >
                {savingRatings
                  ? 'Guardando...'
                  : savedRatings
                    ? '✓ Valoraciones guardadas'
                    : 'Guardar valoraciones'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modal añadir alumno */}
      {addStudentModal && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={() => setAddStudentModal(false)}
        >
          <div
            className="bg-bg-surface border border-bg-border rounded-xl p-6 max-w-md w-full space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-oswald text-lg text-white uppercase tracking-wider">
              Añadir alumno
            </h3>
            <p className="text-text-secondary text-sm">
              Consume una sesión del bono del alumno (FIFO).
            </p>
            <select
              className="input w-full"
              value={addStudentId}
              onChange={(e) => {
                setAddStudentId(e.target.value);
                setAddStudentError('');
              }}
            >
              <option value="">Seleccionar alumno...</option>
              {allStudents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.full_name}
                </option>
              ))}
            </select>
            {addStudentError && <p className="text-error text-sm">{addStudentError}</p>}
            <div className="flex justify-end gap-2">
              <button onClick={() => setAddStudentModal(false)} className="btn-ghost">
                Cancelar
              </button>
              <button
                onClick={handleAddStudent}
                disabled={addingStudent || !addStudentId}
                className="btn-gold"
              >
                {addingStudent ? 'Añadiendo...' : 'Añadir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal info médica */}
      {medicalModal && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={() => setMedicalModal(null)}
        >
          <div
            className="bg-bg-surface border border-bg-border rounded-xl p-6 max-w-md w-full space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-oswald text-lg text-white uppercase tracking-wider">
              Información médica
            </h3>
            <p className="text-white font-medium">{medicalModal.name}</p>
            <div className="bg-error/10 border border-error/40 rounded p-3 text-sm text-white whitespace-pre-wrap">
              {medicalModal.estado === 'cargando'
                ? 'Cargando…'
                : medicalModal.estado === 'error'
                  ? // No se pinta «sin información»: un fallo de lectura que
                    // parece «no tiene nada» deja al entrenador tranquilo por
                    // el motivo equivocado.
                    `No se pudo leer la ficha. ${medicalModal.mensaje ?? ''}`
                  : medicalModal.info || 'Sin información médica registrada.'}
            </div>
            <div className="flex justify-end">
              <button onClick={() => setMedicalModal(null)} className="btn-ghost">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
